// Core financial model — all formulas derived from Excel model spec
// Backend integration point: replace these calculations with API calls if server-side computation is needed

export interface Assumptions {
  // A. Capacity & Operations
  tphCapacity: number;
  operatingHoursPerDay: number;
  operatingDaysPerYear: number;
  year1Utilisation: number; // decimal e.g. 0.75
  year2PlusUtilisation: number; // decimal e.g. 0.95

  // B. Revenue
  sellingPrice: number; // ₹/tonne
  priceEscalation: number; // decimal e.g. 0.03
  carbonCreditsBase: number; // 0 for base case

  // C. Variable Costs (₹/tonne)
  rawMaterialCost: number;
  powerCost: number;
  labourCost: number;
  maintenanceCost: number;
  packagingCost: number;
  otherVariableCost: number;

  // D. Fixed Costs (₹/year)
  landLease: number;
  salaries: number;
  insuranceMisc: number;

  // E. CAPEX (₹)
  machineryCapex: number;
  civilCapex: number;
  electricalCapex: number;
  contingencyCapex: number;
  workingCapital: number;

  // F. Financing
  equityAmount: number;
  debtAmount: number;
  interestRate: number; // decimal e.g. 0.11
  loanTenure: number; // years
  moratorium: number; // years
  depreciationPeriod: number; // years
  taxRate: number; // decimal e.g. 0.25

  // G. Other
  discountRate: number; // decimal e.g. 0.14
  inflationFixedCosts: number; // decimal e.g. 0.04

  // H. Toggles
  isSolarPhase2Enabled?: boolean;
}

export const DEFAULT_ASSUMPTIONS: Assumptions = {
  tphCapacity: 5,
  operatingHoursPerDay: 20,
  operatingDaysPerYear: 300,
  year1Utilisation: 0.75,
  year2PlusUtilisation: 0.95,
  sellingPrice: 8000,
  priceEscalation: 0.03,
  carbonCreditsBase: 0,
  rawMaterialCost: 2500,
  powerCost: 1100,
  labourCost: 350,
  maintenanceCost: 280,
  packagingCost: 150,
  otherVariableCost: 120,
  landLease: 1800000,
  salaries: 4200000,
  insuranceMisc: 900000,
  machineryCapex: 32000000,
  civilCapex: 7500000,
  electricalCapex: 4500000,
  contingencyCapex: 4500000,
  workingCapital: 14000000,
  equityAmount: 25000000,
  debtAmount: 37500000,
  interestRate: 0.11,
  loanTenure: 7,
  moratorium: 1,
  depreciationPeriod: 10,
  taxRate: 0.25,
  discountRate: 0.14,
  inflationFixedCosts: 0.04,
  isSolarPhase2Enabled: false,
};

export interface YearData {
  year: number; // 0 = construction, 1-10 = operating
  production: number;
  utilisation: number;
  revenue: number;
  rawMaterialCost: number;
  powerCost: number;
  labourMaintPackOther: number;
  totalVariableCost: number;
  grossProfit: number;
  landLease: number;
  salaries: number;
  insuranceMisc: number;
  totalFixedCost: number;
  ebitda: number;
  depreciation: number;
  ebit: number;
  interest: number;
  pbt: number;
  tax: number;
  pat: number;
  grossMarginPct: number;
  ebitdaMarginPct: number;
  patMarginPct: number;
  // Cash flow
  projectFcf: number;
  debtMovement: number;
  interestAfterTax: number;
  equityFcf: number;
  principalRepayment: number;
  outstandingDebt: number;
  dscr: number;
}

// IRR calculation using Newton-Raphson
function calcIRR(cashFlows: number[], guess = 0.1): number {
  const maxIter = 1000;
  const tol = 1e-7;
  let rate = guess;
  for (let i = 0; i < maxIter; i++) {
    let npv = 0;
    let dNpv = 0;
    for (let t = 0; t < cashFlows.length; t++) {
      const denom = Math.pow(1 + rate, t);
      npv += cashFlows[t] / denom;
      dNpv -= (t * cashFlows[t]) / (denom * (1 + rate));
    }
    if (Math.abs(dNpv) < 1e-12) break;
    const newRate = rate - npv / dNpv;
    if (Math.abs(newRate - rate) < tol) {
      return newRate;
    }
    rate = newRate;
  }
  return rate;
}

// NPV calculation
function calcNPV(rate: number, cashFlows: number[]): number {
  // cashFlows[0] is Year 0
  let npv = cashFlows[0];
  for (let t = 1; t < cashFlows.length; t++) {
    npv += cashFlows[t] / Math.pow(1 + rate, t);
  }
  return npv;
}

// PMT calculation (like Excel PMT)
function calcPMT(rate: number, nper: number, pv: number): number {
  if (rate === 0) return -pv / nper;
  return (rate * pv) / (1 - Math.pow(1 + rate, -nper));
}

export interface FinancialModel {
  assumptions: Assumptions;
  designCapacity: number;
  totalVariableCostPerTonne: number;
  totalFixedCostBase: number;
  totalProjectCost: number;
  depreciationBase: number;
  annualDepreciation: number;
  annualDebtService: number;
  principalRepaymentPerYear: number;
  years: YearData[];
  projectIRR: number;
  equityIRR: number;
  projectNPV: number;
  paybackYear: number;
  paybackYearFractional: number;
}

export function computeModel(a: Assumptions): FinancialModel {
  // Derived scalars
  const designCapacity = a.tphCapacity * a.operatingHoursPerDay * a.operatingDaysPerYear;
  
  const effectivePowerCost = a.isSolarPhase2Enabled ? a.powerCost * 0.6 : a.powerCost;
  const solarCapex = a.isSolarPhase2Enabled ? 25000000 : 0; // ₹2.5 Cr for ~500kW

  const totalVariableCostPerTonne =
    a.rawMaterialCost +
    effectivePowerCost +
    a.labourCost +
    a.maintenanceCost +
    a.packagingCost +
    a.otherVariableCost;
  const totalFixedCostBase = a.landLease + a.salaries + a.insuranceMisc;
  const baseProjectCost =
    a.machineryCapex + a.civilCapex + a.electricalCapex + a.contingencyCapex + a.workingCapital;
  const totalProjectCost = baseProjectCost + solarCapex;

  // Apportion solar capex using the same D/E ratio
  const debtRatio = baseProjectCost > 0 ? a.debtAmount / baseProjectCost : 0.6;
  const equityRatio = baseProjectCost > 0 ? a.equityAmount / baseProjectCost : 0.4;
  const effectiveDebtAmount = a.debtAmount + solarCapex * debtRatio;
  const effectiveEquityAmount = a.equityAmount + solarCapex * equityRatio;

  const depreciationBase = a.machineryCapex + a.civilCapex + a.electricalCapex + solarCapex;
  const annualDepreciation = depreciationBase / a.depreciationPeriod;
  const repaymentYears = a.loanTenure - a.moratorium;
  const principalRepaymentPerYear = repaymentYears > 0 ? effectiveDebtAmount / repaymentYears : 0;
  const annualDebtService = calcPMT(a.interestRate, repaymentYears, effectiveDebtAmount);

  // Build interest schedule — outstanding balance based
  const interestByYear: number[] = new Array(11).fill(0);
  const outstandingByYear: number[] = new Array(11).fill(0);
  let outstanding = effectiveDebtAmount;
  outstandingByYear[0] = outstanding;
  for (let yr = 1; yr <= 10; yr++) {
    if (yr <= a.moratorium) {
      // interest only
      interestByYear[yr] = outstanding * a.interestRate;
      outstandingByYear[yr] = outstanding;
    } else if (yr <= a.loanTenure) {
      interestByYear[yr] = outstanding * a.interestRate;
      outstanding = Math.max(0, outstanding - principalRepaymentPerYear);
      outstandingByYear[yr] = outstanding;
    } else {
      interestByYear[yr] = 0;
      outstandingByYear[yr] = 0;
    }
  }

  const years: YearData[] = [];

  for (let yr = 0; yr <= 10; yr++) {
    if (yr === 0) {
      years.push({
        year: 0,
        production: 0,
        utilisation: 0,
        revenue: 0,
        rawMaterialCost: 0,
        powerCost: 0,
        labourMaintPackOther: 0,
        totalVariableCost: 0,
        grossProfit: 0,
        landLease: 0,
        salaries: 0,
        insuranceMisc: 0,
        totalFixedCost: 0,
        ebitda: 0,
        depreciation: 0,
        ebit: 0,
        interest: 0,
        pbt: 0,
        tax: 0,
        pat: 0,
        grossMarginPct: 0,
        ebitdaMarginPct: 0,
        patMarginPct: 0,
        projectFcf: -totalProjectCost,
        debtMovement: effectiveDebtAmount,
        interestAfterTax: 0,
        equityFcf: -effectiveEquityAmount,
        principalRepayment: 0,
        outstandingDebt: effectiveDebtAmount,
        dscr: 0,
      });
      continue;
    }

    const util = yr === 1 ? a.year1Utilisation : a.year2PlusUtilisation;
    const production = designCapacity * util;

    // Revenue with escalation: Year 1 no escalation (index 0), Year N: (1+esc)^(N-1)
    const escalationFactor = yr === 1 ? 1 : Math.pow(1 + a.priceEscalation, yr - 1);
    const revenue = production * a.sellingPrice * escalationFactor;

    const rawMat = production * a.rawMaterialCost;
    const power = production * effectivePowerCost;
    const labourMaintPackOther =
      production * (a.labourCost + a.maintenanceCost + a.packagingCost + a.otherVariableCost);
    const totalVC = production * totalVariableCostPerTonne;
    const grossProfit = revenue - totalVC;

    // Fixed costs with inflation
    const inflFactor = yr === 1 ? 1 : Math.pow(1 + a.inflationFixedCosts, yr - 1);
    const landLeaseYr = a.landLease * inflFactor;
    const salariesYr = a.salaries * inflFactor;
    const insuranceYr = a.insuranceMisc * inflFactor;
    const totalFC = landLeaseYr + salariesYr + insuranceYr;

    const ebitda = grossProfit - totalFC;
    const dep = yr <= a.depreciationPeriod ? annualDepreciation : 0;
    const ebit = ebitda - dep;
    const interest = interestByYear[yr];
    const pbt = ebit - interest;
    const tax = Math.max(0, pbt * a.taxRate);
    const pat = pbt - tax;

    const grossMarginPct = revenue > 0 ? grossProfit / revenue : 0;
    const ebitdaMarginPct = revenue > 0 ? ebitda / revenue : 0;
    const patMarginPct = revenue > 0 ? pat / revenue : 0;

    // Cash flows
    const projectFcf = ebitda - tax;

    // Debt movement
    let debtMovement = 0;
    let principalRep = 0;
    if (yr === 1) {
      debtMovement = 0; // moratorium, no repayment
    } else if (yr <= a.loanTenure) {
      principalRep = principalRepaymentPerYear;
      debtMovement = -principalRep;
    }

    const interestAfterTax = interest * (1 - a.taxRate);
    // Equity FCF
    let equityFcf: number;
    if (yr === 1) {
      equityFcf = projectFcf - interestAfterTax;
    } else if (yr <= a.loanTenure) {
      equityFcf = projectFcf + debtMovement - interestAfterTax;
    } else {
      equityFcf = projectFcf;
    }

    // DSCR
    const debtService = interest + principalRep;
    const dscr = debtService > 0 ? ebitda / debtService : 0;

    years.push({
      year: yr,
      production,
      utilisation: util,
      revenue,
      rawMaterialCost: rawMat,
      powerCost: power,
      labourMaintPackOther,
      totalVariableCost: totalVC,
      grossProfit,
      landLease: landLeaseYr,
      salaries: salariesYr,
      insuranceMisc: insuranceYr,
      totalFixedCost: totalFC,
      ebitda,
      depreciation: dep,
      ebit,
      interest,
      pbt,
      tax,
      pat,
      grossMarginPct,
      ebitdaMarginPct,
      patMarginPct,
      projectFcf,
      debtMovement,
      interestAfterTax,
      equityFcf,
      principalRepayment: principalRep,
      outstandingDebt: outstandingByYear[yr],
      dscr,
    });
  }

  // IRR/NPV
  const projectFcfs = years.map((y) => y.projectFcf);
  const equityFcfs = years.map((y) => y.equityFcf);
  const projectIRR = calcIRR(projectFcfs, 0.5);
  const equityIRR = calcIRR(equityFcfs, 1.0);
  const projectNPV = calcNPV(a.discountRate, projectFcfs);

  // Payback: cumulative project FCF turns positive
  let cumulative = 0;
  let paybackYear = -1;
  let paybackYearFractional = -1;
  for (let i = 0; i < projectFcfs.length; i++) {
    const prev = cumulative;
    cumulative += projectFcfs[i];
    if (prev < 0 && cumulative >= 0 && i > 0) {
      paybackYear = i;
      paybackYearFractional = i - 1 + Math.abs(prev) / projectFcfs[i];
      break;
    }
  }

  return {
    assumptions: a,
    designCapacity,
    totalVariableCostPerTonne,
    totalFixedCostBase,
    totalProjectCost,
    depreciationBase,
    annualDepreciation,
    annualDebtService,
    principalRepaymentPerYear,
    years,
    projectIRR,
    equityIRR,
    projectNPV,
    paybackYear,
    paybackYearFractional,
  };
}

// Formatting utilities
export function fmtCr(value: number, decimals = 2): string {
  const cr = value / 10000000;
  return `₹${cr.toFixed(decimals)} Cr`;
}

export function fmtLakh(value: number, decimals = 1): string {
  const lakh = value / 100000;
  return `₹${lakh.toFixed(decimals)}L`;
}

export function fmtIndian(value: number): string {
  if (value === 0) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const str = Math.round(abs).toString();
  let result = '';
  const len = str.length;
  if (len <= 3) {
    result = str;
  } else {
    result = str.slice(len - 3);
    let remaining = str.slice(0, len - 3);
    while (remaining.length > 2) {
      result = remaining.slice(-2) + ',' + result;
      remaining = remaining.slice(0, -2);
    }
    result = remaining + ',' + result;
  }
  return `${sign}₹${result}`;
}

export function fmtPct(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function fmtNum(value: number, decimals = 0): string {
  if (value === 0) return '—';
  return value.toLocaleString('en-IN', { maximumFractionDigits: decimals });
}

export function fmtCrShort(value: number): string {
  const abs = Math.abs(value);
  const cr = abs / 10000000;
  const sign = value < 0 ? '-' : '';
  if (cr >= 100) return `${sign}₹${cr.toFixed(0)} Cr`;
  if (cr >= 10) return `${sign}₹${cr.toFixed(1)} Cr`;
  return `${sign}₹${cr.toFixed(2)} Cr`;
}

export interface ScenarioDrivers {
  sellingPrice: number;
  year1Utilisation: number;
  year2PlusUtilisation: number;
  rawMaterialCost: number;
  powerCost: number;
  totalProjectCost: number;
}

export const SCENARIO_DRIVERS: Record<'downside' | 'base' | 'upside', ScenarioDrivers> = {
  downside: {
    sellingPrice: 7000,
    year1Utilisation: 0.55,
    year2PlusUtilisation: 0.8,
    rawMaterialCost: 3000,
    powerCost: 1300,
    totalProjectCost: 70000000,
  },
  base: {
    sellingPrice: 8000,
    year1Utilisation: 0.75,
    year2PlusUtilisation: 0.95,
    rawMaterialCost: 2500,
    powerCost: 1100,
    totalProjectCost: 62500000,
  },
  upside: {
    sellingPrice: 9000,
    year1Utilisation: 0.85,
    year2PlusUtilisation: 1.0,
    rawMaterialCost: 2200,
    powerCost: 900,
    totalProjectCost: 55000000,
  },
};

export function applyScenario(
  base: Assumptions,
  scenario: 'downside' | 'base' | 'upside'
): Assumptions {
  const d = SCENARIO_DRIVERS[scenario];
  // For totalProjectCost in scenario, we scale CAPEX items proportionally
  const currentCapex =
    base.machineryCapex +
    base.civilCapex +
    base.electricalCapex +
    base.contingencyCapex +
    base.workingCapital;
  const scaleFactor = currentCapex > 0 ? d.totalProjectCost / currentCapex : 1;
  return {
    ...base,
    sellingPrice: d.sellingPrice,
    year1Utilisation: d.year1Utilisation,
    year2PlusUtilisation: d.year2PlusUtilisation,
    rawMaterialCost: d.rawMaterialCost,
    powerCost: d.powerCost,
    machineryCapex: Math.round(base.machineryCapex * scaleFactor),
    civilCapex: Math.round(base.civilCapex * scaleFactor),
    electricalCapex: Math.round(base.electricalCapex * scaleFactor),
    contingencyCapex: Math.round(base.contingencyCapex * scaleFactor),
    workingCapital: Math.round(base.workingCapital * scaleFactor),
    equityAmount: Math.round(d.totalProjectCost * 0.4),
    debtAmount: Math.round(d.totalProjectCost * 0.6),
  };
}
